/**
 * useOAuthPopup Hook
 * 
 * React hook for OAuth popup authentication
 * Provides simple interface for opening OAuth popups and handling responses
 * 
 * @hook
 */

"use client";

import { useState, useRef, useEffect, useCallback } from 'react';
import OAuthPopupManager from '@/lib/oauth/OAuthPopupManager';

export function useOAuthPopup() {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);
  const managerRef = useRef(null);

  /**
   * Open OAuth popup
   * @param {string} oauthUrl - OAuth URL to open
   * @param {string} provider - Provider name (for display)
   * @returns {Promise<Object>} Result object
   */
  const openOAuthPopup = useCallback(async (oauthUrl, provider = 'OAuth') => {
    console.log(`[useOAuthPopup] Opening ${provider} authentication`);
    setIsAuthenticating(true);
    setCurrentProvider(provider);

    const manager = new OAuthPopupManager({
      onSuccess: (data) => {
        console.log(`[useOAuthPopup] ${provider} authentication successful:`, data);
        setIsAuthenticating(false);
        setCurrentProvider(null);
      },
      onError: (error) => {
        console.error(`[useOAuthPopup] ${provider} authentication failed:`, error);
        setIsAuthenticating(false);
        setCurrentProvider(null);
      },
      onClose: () => {
        console.log(`[useOAuthPopup] ${provider} popup closed`);
        setIsAuthenticating(false);
        setCurrentProvider(null);
      }
    });

    managerRef.current = manager;

    try {
      const result = await manager.openPopup(oauthUrl, `Connect ${provider}`);
      console.log(`[useOAuthPopup] ${provider} result:`, result);
      return result;
    } catch (error) {
      console.error(`[useOAuthPopup] ${provider} error:`, error);
      setIsAuthenticating(false);
      setCurrentProvider(null);
      throw error;
    }
  }, []);

  /**
   * Close current popup
   */
  const closePopup = useCallback(() => {
    if (managerRef.current) {
      managerRef.current.closePopup();
      setIsAuthenticating(false);
      setCurrentProvider(null);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (managerRef.current) {
        managerRef.current.destroy();
      }
    };
  }, []);

  return {
    openOAuthPopup,
    closePopup,
    isAuthenticating,
    currentProvider,
  };
}

export default useOAuthPopup;





